import { useState, useMemo, useEffect, useRef } from 'react';
import { Search, MapPin, X } from 'lucide-react';
import { REGIONS } from '../../data/mockData';
import { useI18n } from '../../i18n';

interface SubregionOption {
  id: string;
  name: string;
  regionName: string;
  lat?: number;
  lng?: number;
}

interface SubregionSearchProps {
  value: string;
  regionValue: string;
  onChange: (city: string, region: string, lat?: number, lng?: number) => void;
  className?: string;
  placeholder?: string;
}

// Normalize string for search (remove diacritics)
function normalize(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/ć/g, 'c')
    .replace(/č/g, 'c')
    .replace(/š/g, 's')
    .replace(/ž/g, 'z');
}

export function SubregionSearch({ 
  value, 
  regionValue,
  onChange, 
  className, 
  placeholder 
}: SubregionSearchProps) {
  const { language } = useI18n();
  const [query, setQuery] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Build flat list of all subregions (cities) with their parent regions
  const allSubregions = useMemo(() => {
    const list: SubregionOption[] = [];
    REGIONS.forEach(region => {
      region.cities.forEach(city => {
        list.push({
          id: `${region.id}-${city.id}`,
          name: city.name,
          regionName: region.name,
          lat: city.lat,
          lng: city.lng
        });
      });
    });
    return list;
  }, []);

  // Filter subregions based on query
  const filteredSubregions = useMemo(() => {
    if (!query.trim()) {
      // If region is selected, show only cities from that region
      if (regionValue) {
        return allSubregions.filter(s => s.regionName === regionValue);
      }
      return allSubregions;
    }

    const normalizedQuery = normalize(query);
    return allSubregions.filter(s => 
      normalize(s.name).includes(normalizedQuery)
    );
  }, [query, allSubregions, regionValue]);

  // Update query when value prop changes
  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  // Handle selection
  const handleSelect = (subregion: SubregionOption) => {
    setQuery(subregion.name);
    setIsOpen(false);
    onChange(subregion.name, subregion.regionName, subregion.lat, subregion.lng);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(i => Math.min(i + 1, filteredSubregions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(i => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredSubregions[highlightedIndex]) {
          handleSelect(filteredSubregions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && listRef.current) {
      const item = listRef.current.children[highlightedIndex] as HTMLElement;
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Reset highlighted index when filtered results change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredSubregions]);

  const handleClear = () => {
    setQuery('');
    onChange('', '', undefined, undefined);
    inputRef.current?.focus();
  };

  return (
    <div className={`relative ${className || ''}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={() => {
            // Delay closing to allow click events
            setTimeout(() => setIsOpen(false), 200);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || (language === 'sr' ? 'Pretraži grad/opštinu...' : 'Search city/municipality...')}
          className="w-full pl-10 pr-10 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
        />
        {query && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {isOpen && filteredSubregions.length > 0 && (
        <ul
          ref={listRef}
          className="absolute z-50 w-full mt-1 max-h-60 overflow-auto rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg"
        >
          {filteredSubregions.map((subregion, index) => (
            <li
              key={subregion.id}
              onClick={() => handleSelect(subregion)}
              className={`px-4 py-2 cursor-pointer flex items-center justify-between ${
                index === highlightedIndex 
                  ? 'bg-amber-100 dark:bg-amber-900/30' 
                  : 'hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-amber-500" />
                <span className="text-slate-900 dark:text-white font-medium">
                  {subregion.name}
                </span>
              </div>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {subregion.regionName}
              </span>
            </li>
          ))}
        </ul>
      )}

      {isOpen && query && filteredSubregions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 p-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg text-center text-slate-500">
          {language === 'sr' ? 'Nema rezultata' : 'No results'}
        </div>
      )}
    </div>
  );
}
